import base from "lib/base";
import { sanitizeText } from "lib/sanitize";
import { getSiteSeoConfig } from "lib/site-seo";
import { searchEngineRobots } from "lib/searchEngineSeo";

export function buildDetailUrl(path) {
  const normalizedPath = String(path || "").replace(/^\/+/, "");
  const baseUrl = String(base.baseUrl || "").replace(/\/+$/, "");
  return normalizedPath ? `${baseUrl}/${normalizedPath}` : baseUrl;
}

export function buildCdnUrl(pathname) {
  if (!pathname) return "";
  const str = String(pathname).trim();
  if (/^https?:\/\//i.test(str)) return str;
  const normalizedBase = base.cdnUrl.endsWith("/")
    ? base.cdnUrl
    : `${base.cdnUrl}/`;
  const normalizedPath = str.replace(/^\/+/, "");
  return new URL(normalizedPath, normalizedBase).toString();
}

export function getDefaultOgImageUrl() {
  return buildDetailUrl("assets/img/default.png");
}

export function buildOgImageUrl(path) {
  if (path) {
    const resolved = buildCdnUrl(path);
    if (resolved) return resolved;
  }
  return getDefaultOgImageUrl();
}

export function resolveBookImagePath(book) {
  if (!book || typeof book !== "object") return null;

  // images[] ихэвчлэн номын хавтас; cover заримдаа 5MB+ декор зураг (FB preview алдагдуулна)
  const candidates = [
    book.picture,
    ...(Array.isArray(book.images) ? book.images : []),
    ...(Array.isArray(book.pictures) ? book.pictures : []),
    book.cover,
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === "string" && candidate.trim()) return candidate.trim();
    if (typeof candidate === "object") {
      const nested =
        candidate.url || candidate.path || candidate.src || candidate.file;
      if (nested && String(nested).trim()) return String(nested).trim();
    }
  }

  return null;
}

export function buildOgImageMetadata(path, alt = "") {
  const url = buildOgImageUrl(path);
  return {
    url,
    width: 1200,
    height: 630,
    alt: alt || "Gerelt",
  };
}

export function resolveMediaUrl(value) {
  if (!value) return null;
  const str = String(value).trim();
  if (!str) return null;
  if (/^https?:\/\//i.test(str)) return str;
  return buildCdnUrl(str);
}

export function truncateDescription(text, maxLength = 180) {
  const cleaned = String(text || "").trim();
  if (!cleaned) return "";
  if (cleaned.length <= maxLength) return cleaned;
  return `${cleaned.substring(0, maxLength).trim()}...`;
}

export function getResolvedSiteTitle(info) {
  const resolved = Array.isArray(info) ? info[0] || {} : info || {};
  const core = resolved?.core || resolved || {};
  const translation = resolved?.translation || {};

  return (
    translation?.siteTitle ||
    core?.siteTitle ||
    resolved?.siteTitle ||
    "Gerelt"
  );
}

/**
 * Detail хуудсын metadata — parent openGraph-ийг БҮРЭН солино (merge биш override)
 */
export async function buildPageMetadata({
  title,
  openGraphTitle,
  description,
  detailUrl,
  ogImage,
  ogImagePath,
  type = "article",
  siteTitle,
}) {
  const site = await getSiteSeoConfig();
  const resolvedDescription = description || site.description;
  const resolvedTitle = title || site.brandName;
  const resolvedOgTitle = openGraphTitle || resolvedTitle;
  const imageMeta = buildOgImageMetadata(ogImagePath, resolvedOgTitle);
  const resolvedImageUrl = ogImage || imageMeta.url;
  const imageObject = {
    ...imageMeta,
    url: resolvedImageUrl,
    alt: imageMeta.alt || resolvedOgTitle,
  };
  const openGraphType = type === "book" ? "website" : type;

  return {
    metadataBase: new URL(site.metadataBaseUrl),
    title: resolvedTitle,
    description: resolvedDescription,
    robots: searchEngineRobots,
    alternates: detailUrl ? { canonical: detailUrl } : undefined,
    openGraph: {
      title: resolvedOgTitle,
      description: resolvedDescription,
      url: detailUrl,
      siteName: siteTitle || site.brandName,
      locale: "mn_MN",
      images: [imageObject],
      type: openGraphType,
    },
    twitter: {
      card: "summary_large_image",
      title: resolvedOgTitle,
      description: resolvedDescription,
      images: [resolvedImageUrl],
      site: "@gerelt",
      creator: "@gerelt",
    },
  };
}

function buildBookMetadataObject(book, slug) {
  const canonicalSlug = book.slug || slug;
  const detailUrl = buildDetailUrl(`books/${canonicalSlug}`);
  const plainDescription =
    truncateDescription(
      sanitizeText(book.intro || book.description || ""),
      180,
    ) || "Gerelt Publishing";
  const title = book.name || "Номын дэлгэрэнгүй";

  return {
    title,
    openGraphTitle: book.title || book.name || title,
    description: plainDescription,
    detailUrl,
    ogImagePath: resolveBookImagePath(book),
    type: "book",
  };
}

export async function buildBookPageMetadata(book, slug) {
  return buildPageMetadata(buildBookMetadataObject(book, slug));
}

export async function buildBookNotFoundMetadata(slug) {
  const site = await getSiteSeoConfig();
  const detailUrl = buildDetailUrl(`books/${slug}`);
  const title = "Ном олдсонгүй";

  return {
    metadataBase: new URL(site.metadataBaseUrl),
    title,
    description: title,
    robots: { index: false, follow: false },
    alternates: { canonical: detailUrl },
    openGraph: {
      title,
      description: "Хүссэн ном олдсонгүй.",
      url: detailUrl,
      siteName: site.brandName,
      locale: "mn_MN",
      type: "website",
      images: [
        {
          url: getDefaultOgImageUrl(),
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
    },
    twitter: {
      card: "summary",
      title,
      description: "Хүссэн ном олдсонгүй.",
    },
  };
}
